<template>
    <div class="form-group">
        <label>{{ $t('dosages.form.dose') }}</label>
        <range-input
            :range="formulationAndRoute.dosageAvailableDoses.values"
            :default="formulationAndRoute.dosageAvailableDoses.defaultValue"
            :is-free="true"
            v-model="dosage.doseValue"
            :unit="formulationAndRoute.dosageAvailableDoses.unit"
            absolute-min="0.01"
        />
    </div>

    <div class="form-group">
        <label>{{ $t('dosages.form.interval') }}</label>
        <range-input
            :range="formulationAndRoute.dosageAvailableIntervals.values"
            :default="formulationAndRoute.dosageAvailableIntervals.defaultValue"
            :is-free="true"
            v-model="dosage.intervalValue"
            :unit="formulationAndRoute.dosageAvailableIntervals.unit"
            absolute-min="0.01"
        />
    </div>

    <div class="form-group" v-if="formulationAndRoute.dosageAvailableInfusions">
        <label>{{ $t('dosages.form.infusion') }}</label>
        <range-input
            :range="formulationAndRoute.dosageAvailableInfusions.values"
            :default="formulationAndRoute.dosageAvailableInfusions.defaultValue"
            :is-free="true"
            v-model="dosage.doseInfusionTimeValue"
            :unit="formulationAndRoute.dosageAvailableInfusions.unit"
            absolute-min="0.01"
        />
    </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, watch, watchEffect } from 'vue';
import RangeInput from '@/components/RangeInput.vue';
import { FormulationAndRouteFull } from '@/service/models/response/drug-model-detail/formulation-and-route-full';
import { absorptionModelDrugToComputation } from '@/service/models/common/parts/formulation-and-route-enums';
import { LastingDosageVM } from '@/service/view-models/dosage-choices/lasting-dosage-vm';

export default defineComponent({
    components: {
        RangeInput,
    },

    props: {
        modelValue: {
            type: Object as PropType<LastingDosageVM>,
            required: true,
        },

        formulationAndRoute: {
            type: Object as PropType<FormulationAndRouteFull>,
            required: true,
        }
    },

    emits: {
        "update:modelValue": (payload: LastingDosageVM) => typeof payload === "object",
    },

    setup(props, {emit})
    {
        const dosage = ref<LastingDosageVM>(props.modelValue);

        // Using watchEffect() allows to re-run this callback if the props are modified
        watchEffect(() => {
            dosage.value.doseUnit = props.formulationAndRoute.dosageAvailableDoses.unit;
            dosage.value.intervalUnit = props.formulationAndRoute.dosageAvailableIntervals.unit;

            dosage.value.formulation = props.formulationAndRoute.formulation;
            dosage.value.administrationName = props.formulationAndRoute.administrationName;
            dosage.value.administrationRoute = props.formulationAndRoute.administrationRoute;
            dosage.value.absorptionModel = absorptionModelDrugToComputation(props.formulationAndRoute.absorptionModel);

            dosage.value.doseInfusionTimeUnit = props.formulationAndRoute.dosageAvailableInfusions?.unit ?? null;
        });

        // Emit the change in model value each time it is modified by the user.
        // This is actually not required because the object reference is shared, but
        // theoretically it should be isolated and the change should be propagated by an event like this.
        watch(
            dosage,
            (newValue) => emit("update:modelValue", newValue as LastingDosageVM),
            { deep: true }
        );

        return { dosage };
    },
})
</script>
